import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import DescriptionIcon from '@mui/icons-material/Description';
import HistoryIcon from '@mui/icons-material/History';

import {
  SectionContent,
  GoBackButton,
  StyledList,
  ContactLink
} from '../../components';
import ChartHistoryDrawer from '../../components/common/ChartHistoryDrawer';
import SEO from '../../components/SEO';
import { seoData } from '../../data/seoData';
import { openPDF } from '../../utils';
import useStrategyAssets from '../../hooks/useStrategyAssets';
import useStrategyPerformance from '../../hooks/useStrategyPerformance';
import ROUTES from '../../config/routes';
import THEME from '../../constants/theme';
import {
  SECTION_SPACING,
  SUBSECTION_SPACING,
  ELEMENT_SPACING
} from '../../constants';
import strandQLogo from '../../assets/icons/strand-q-logo.svg';

const StrandQPage = () => {
  const { latestCharts, chartHistory, documents } =
    useStrategyAssets('strand-q');
  const { performance: strategyPerformance } =
    useStrategyPerformance('strand-q');
  const [chartView, setChartView] = useState('week');
  const [chartType, setChartType] = useState('performance');
  const [isLoading, setIsLoading] = useState(false);
  const [displayedSrc, setDisplayedSrc] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyChart, setHistoryChart] = useState(null);

  // Set initial chart once CMS assets load (ref prevents stale-closure lint warning)
  const hasInitialized = useRef(false);
  useEffect(() => {
    if (!hasInitialized.current && latestCharts?.week?.performance) {
      hasInitialized.current = true;
      setDisplayedSrc(latestCharts.week.performance);
    }
  }, [latestCharts]);

  const getChartSrc = (view, type) => {
    const period = view === 'week' ? 'week' : 'month';
    return latestCharts?.[period]?.[type] ?? null;
  };

  const updateChart = (nextView, nextType) => {
    const viewToUse = nextView || chartView;
    const typeToUse = nextType || chartType;
    const nextSrc = getChartSrc(viewToUse, typeToUse);

    if (!nextSrc || nextSrc === displayedSrc) return;

    setIsLoading(true);
    const img = new Image();
    img.onload = () => {
      setDisplayedSrc(nextSrc);
      setIsLoading(false);
    };
    img.onerror = () => setIsLoading(false);
    img.src = nextSrc;
  };

  const handleChartViewChange = (e, v) => {
    if (!v || v === chartView) return;
    setChartView(v);
    updateChart(v, chartType);
  };

  const handleChartTypeChange = (e, v) => {
    if (!v || v === chartType) return;
    setChartType(v);
    updateChart(chartView, v);
  };

  const handleDocumentClick = docType => {
    switch (docType) {
      case 'full-factsheet':
        if (documents.factsheet) openPDF(documents.factsheet);
        break;
      case 'strategy-factsheet':
        if (documents.description) openPDF(documents.description);
        break;
      default:
        break;
    }
  };

  const fallbackPerformanceStats = [
    { label: 'Average ROR', value: '2.00%' },
    { label: 'Sharpe Ratio (1Y- Bill)', value: '1.17' },
    { label: 'Total Return Cumulative', value: '59.26%' },
    { label: 'Sortino Ratio (1Y T-Bill)', value: '2.49' },
    { label: 'Total Return Annualized', value: '25.03%' },
    { label: 'Excess Return', value: '3.72%' }
  ];

  const performanceStats =
    strategyPerformance?.performanceStats?.length > 0
      ? strategyPerformance.performanceStats
      : fallbackPerformanceStats;

  const heroTagline =
    strategyPerformance?.hero?.tagline ||
    'Systematic Alpha with Preserved Capital';

  return (
    <>
      <SEO
        title={seoData.strandQ.title}
        content={seoData.strandQ.content}
        keywords={seoData.strandQ.keywords}
        canonical={seoData.strandQ.canonical}
        ogImage={seoData.strandQ.ogImage}
        ogType={seoData.strandQ.ogType}
        structuredData={seoData.strandQ.structuredData}
      />
      <Box sx={{ width: '100%', backgroundColor: THEME.COLORS.NEUTRAL_WHITE }}>
        <GoBackButton backTo={ROUTES.STRATEGIES.ROOT} backText='Strategies' />

        {/* Hero Section with Logo */}
        <Box id='hero' sx={{ mb: SECTION_SPACING }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: THEME.SPACING.SMALL
            }}
          >
            <img
              src={strandQLogo}
              alt='Strand Q Logo'
              style={{
                height: '110px',
                objectFit: 'contain'
              }}
            />
          </Box>
          <Typography
            sx={{
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
              fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.6rem' },
              color: THEME.COLORS.NEUTRAL_GRAY_DARK,
              fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.MEDIUM
            }}
          >
            {heroTagline}
          </Typography>
        </Box>

        {/* Performance Overview Section */}
        <Box sx={{ mb: SECTION_SPACING }}>
          <Typography
            variant='h2'
            sx={{
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
              fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.BOLD,
              fontSize: { xs: '1.8rem', sm: '2rem', md: '2.2rem' },
              color: THEME.COLORS.PRIMARY_BLUE,
              mb: ELEMENT_SPACING
            }}
          >
            Performance Overview
          </Typography>

          <SectionContent
            initialSpacing={false}
            text={[
              {
                text: "The chart below highlights STRAND Q's consistent return profile, adaptive risk control, and ability to navigate volatile regimes through disciplined execution.",
                fontSize: '1.3rem',
                marginBottom: ELEMENT_SPACING,
                color: THEME.COLORS.NEUTRAL_GRAY_DARK
              }
            ]}
          />

          {/* Performance Chart with Weekly/Monthly toggle */}
          <Box
            sx={{
              mb: ELEMENT_SPACING,
              backgroundColor: '#f8f9fb',
              borderRadius: '8px',
              padding: { xs: 2, sm: 3, md: 4 },
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
            }}
          >
            {/* ── Chart toggles – hidden while viewing a history chart ── */}
            {!historyChart && (
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1.5,
                  mb: 1.5
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.2,
                    justifyContent: 'flex-start',
                    flex: 1,
                    minWidth: 240
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.9rem',
                      color: THEME.COLORS.NEUTRAL_GRAY_DARK,
                      fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.MEDIUM
                    }}
                  >
                    Chart Type
                  </Typography>
                  <ToggleButtonGroup
                    value={chartType}
                    exclusive
                    onChange={handleChartTypeChange}
                    size='small'
                    aria-label='Strand Q chart type'
                    sx={{
                      '& .MuiToggleButton-root': {
                        textTransform: 'none',
                        fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.MEDIUM,
                        px: 1.6,
                        py: 0.55,
                        borderColor: 'transparent',
                        backgroundColor: '#e8f0ff',
                        color: THEME.COLORS.PRIMARY_BLUE,
                        borderRadius: '4px',
                        marginLeft: 0.35,
                        '&:hover': {
                          backgroundColor: THEME.COLORS.PRIMARY_BLUE + '24',
                          borderColor: THEME.COLORS.PRIMARY_BLUE
                        }
                      },
                      '& .MuiToggleButton-root.Mui-selected': {
                        background: `linear-gradient(135deg, ${THEME.COLORS.PRIMARY_BLUE}, #1b84ff)`,
                        color: THEME.COLORS.NEUTRAL_WHITE,
                        borderColor: THEME.COLORS.PRIMARY_BLUE,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        '&:hover': {
                          background: THEME.COLORS.PRIMARY_BLUE,
                          color: THEME.COLORS.NEUTRAL_WHITE
                        }
                      }
                    }}
                  >
                    <ToggleButton
                      value='performance'
                      aria-label='Performance chart'
                    >
                      Performance
                    </ToggleButton>
                    <ToggleButton value='exposure' aria-label='Exposure chart'>
                      Exposure
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                <ToggleButtonGroup
                  value={chartView}
                  exclusive
                  onChange={handleChartViewChange}
                  size='small'
                  aria-label='Strand Q chart timeframe'
                  sx={{
                    '& .MuiToggleButton-root': {
                      textTransform: 'none',
                      px: 1.6,
                      py: 0.55,
                      borderColor: 'transparent',
                      backgroundColor: '#e8f0ff',
                      color: THEME.COLORS.PRIMARY_BLUE,
                      borderRadius: '4px',
                      marginLeft: 0.35,
                      '&:hover': {
                        backgroundColor: THEME.COLORS.PRIMARY_BLUE + '24',
                        borderColor: THEME.COLORS.PRIMARY_BLUE
                      }
                    },
                    '& .MuiToggleButton-root.Mui-selected': {
                      background: `linear-gradient(135deg, ${THEME.COLORS.PRIMARY_BLUE}, #1b84ff)`,
                      color: THEME.COLORS.NEUTRAL_WHITE,
                      borderColor: THEME.COLORS.PRIMARY_BLUE,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      '&:hover': {
                        background: THEME.COLORS.PRIMARY_BLUE,
                        color: THEME.COLORS.NEUTRAL_WHITE
                      }
                    }
                  }}
                >
                  <ToggleButton value='week' aria-label='Weekly chart'>
                    Weekly
                  </ToggleButton>
                  <ToggleButton value='month' aria-label='Monthly chart'>
                    Monthly
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}

            {/* ── History toolbar – below chart type buttons ──────────────────── */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: historyChart ? 'space-between' : 'flex-start',
                mb: historyChart ? 1.5 : 1,
                mt: !historyChart ? 1.5 : 0
              }}
            >
              <Button
                size='small'
                variant={historyOpen ? 'contained' : 'outlined'}
                startIcon={<HistoryIcon />}
                onClick={() => setHistoryOpen(h => !h)}
                aria-expanded={historyOpen}
                aria-controls='strand-q-history'
                sx={{
                  textTransform: 'none',
                  px: 1.4,
                  py: 0.5,
                  borderColor: THEME.COLORS.PRIMARY_BLUE,
                  color: historyOpen
                    ? THEME.COLORS.NEUTRAL_WHITE
                    : THEME.COLORS.PRIMARY_BLUE,
                  backgroundColor: historyOpen
                    ? THEME.COLORS.PRIMARY_BLUE
                    : 'transparent',
                  fontSize: '0.82rem',
                  fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.MEDIUM,
                  '&:hover': {
                    backgroundColor: historyOpen
                      ? THEME.COLORS.PRIMARY_BLUE
                      : THEME.COLORS.PRIMARY_BLUE + '14',
                    borderColor: THEME.COLORS.PRIMARY_BLUE
                  }
                }}
              >
                History
              </Button>
              {historyChart && (
                <Typography
                  sx={{
                    fontSize: '0.85rem',
                    color: THEME.COLORS.NEUTRAL_GRAY,
                    fontStyle: 'italic'
                  }}
                >
                  Viewing:&nbsp;
                  <Box
                    component='span'
                    sx={{
                      fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.SEMI_BOLD,
                      fontStyle: 'normal',
                      color: THEME.COLORS.NEUTRAL_GRAY_DARK
                    }}
                  >
                    {historyChart}
                  </Box>
                </Typography>
              )}
            </Box>

            <ChartHistoryDrawer
              id='strand-q-history'
              open={historyOpen}
              onClose={() => setHistoryOpen(false)}
              chartHistory={chartHistory}
              onSelectChart={(url, label) => {
                setDisplayedSrc(url);
                setHistoryChart(label);
                setHistoryOpen(true);
              }}
            />
            <Box
              sx={{
                position: 'relative',
                minHeight: { xs: 200, sm: 260, md: 320 }
              }}
              aria-busy={isLoading || !displayedSrc}
            >
              <Box
                component='img'
                src={displayedSrc || ''}
                alt={`Strand Q Chart - ${
                  chartType === 'performance' ? 'Performance' : 'Exposure'
                } ${chartView === 'week' ? 'Weekly' : 'Monthly'}`}
                sx={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                  borderRadius: '4px',
                  opacity: isLoading || !displayedSrc ? 0 : 1,
                  transition: 'opacity 0.2s ease'
                }}
                onLoad={() => setIsLoading(false)}
                onError={() => setIsLoading(false)}
              />
              {(isLoading || !displayedSrc) && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <CircularProgress
                    sx={{ color: THEME.COLORS.PRIMARY_BLUE }}
                    size={32}
                  />
                </Box>
              )}
            </Box>
          </Box>

          {/* Performance Statistics */}
          <Box
            sx={{
              backgroundColor: THEME.COLORS.NEUTRAL_WHITE,
              border: `1px solid ${THEME.COLORS.NEUTRAL_GRAY_LIGHT}`,
              borderRadius: '8px',
              padding: { xs: 3, sm: 4 },
              mb: ELEMENT_SPACING
            }}
          >
            <Typography
              sx={{
                fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                fontSize: '1.5rem',
                fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.BOLD,
                color: THEME.COLORS.PRIMARY_BLUE,
                mb: 3,
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
            >
              Performance Statistics
            </Typography>
            <Grid container spacing={3}>
              {performanceStats.map(stat => (
                <Grid item xs={12} sm={6} md={4} key={stat.label}>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: '1rem',
                        color: THEME.COLORS.NEUTRAL_GRAY_DARK,
                        mb: 0.5
                      }}
                    >
                      {stat.label}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '2.5rem',
                        fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.BOLD,
                        color: THEME.COLORS.NEUTRAL_BLACK
                      }}
                    >
                      {stat.value}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>

        {/* About STRAND Q Section */}
        <Box sx={{ mb: SECTION_SPACING }}>
          <Typography
            variant='h2'
            sx={{
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
              fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.BOLD,
              fontSize: { xs: '1.8rem', sm: '2rem', md: '2.2rem' },
              color: THEME.COLORS.PRIMARY_BLUE,
              mb: ELEMENT_SPACING
            }}
          >
            About STRAND Q
          </Typography>
          <SectionContent
            initialSpacing={false}
            text={[
              {
                text: "STRAND Q is MarQ Capital's flagship systematic strategy, designed for institutional portfolios seeking low-correlation diversification and daily liquidity.",
                fontSize: '1.3rem',
                marginBottom: ELEMENT_SPACING
              },
              {
                text: 'Built on decades of quantitative research and real-time execution infrastructure, the strategy delivers adaptive exposure across FX and multi-asset markets, with capital preservation at its core.',
                fontSize: '1.3rem',
                marginBottom: SECTION_SPACING
              }
            ]}
          />
        </Box>

        {/* Key Features Section */}
        <Box sx={{ mb: SECTION_SPACING }}>
          <Typography
            variant='h2'
            sx={{
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
              fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.BOLD,
              fontSize: { xs: '1.8rem', sm: '2rem', md: '2.2rem' },
              color: THEME.COLORS.PRIMARY_BLUE,
              mb: ELEMENT_SPACING
            }}
          >
            Key Features
          </Typography>

          <Box sx={{ mb: SUBSECTION_SPACING }}>
            <StyledList
              variant='default'
              items={[
                {
                  TITLE: '1. Data-Driven Discipline',
                  TEXT: 'Every position is generated by proprietary quantitative signals, rigorously back-tested, systematically executed, and constantly refined as market conditions evolve.'
                },
                {
                  TITLE: '2. Macro-Agnostic Exposure',
                  TEXT: 'STRAND Q operates independently of discretionary macro bias. Its models adapt fluidly across economic cycles, focusing on opportunity and signal strength rather than prediction.'
                },
                {
                  TITLE: '3. Diversification & Correlation Control',
                  TEXT: 'The strategy is designed to provide returns that move differently from equities and bonds, enhancing portfolio resilience through low-correlation alpha.'
                },
                {
                  TITLE: '4. Institutional Integrity',
                  TEXT: 'Investor capital remains segregated at regulated custodians and prime brokers, with execution through bankruptcy-remote AMCs and segregated account mandates (SMAs).'
                }
              ]}
              titleTag='h3'
              titleVariant='h3'
            />
          </Box>
        </Box>

        {/* Performance & Risk Section */}
        <Box sx={{ mb: SECTION_SPACING }}>
          <Typography
            variant='h2'
            sx={{
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
              fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.BOLD,
              fontSize: { xs: '1.8rem', sm: '2rem', md: '2.2rem' },
              color: THEME.COLORS.PRIMARY_BLUE,
              mb: ELEMENT_SPACING
            }}
          >
            Performance & Risk
          </Typography>
          <SectionContent
            initialSpacing={false}
            text={[
              {
                text: "STRAND Q's live performance demonstrates:",
                fontSize: '1.3rem',
                marginBottom: ELEMENT_SPACING
              }
            ]}
          />
          <Box
            sx={{
              paddingLeft: {
                xs: THEME.SPACING.MEDIUM,
                sm: THEME.SPACING.MEDIUM,
                md: THEME.SPACING.XL
              },
              mb: ELEMENT_SPACING
            }}
          >
            <StyledList
              variant='blue'
              items={[
                { TEXT: 'Consistent compounding through volatile conditions' },
                { TEXT: 'Controlled drawdowns within pre-defined risk bands' },
                {
                  TEXT: 'Sharpe ratio above benchmark peers in systematic FX/multi-asset space'
                }
              ]}
            />
          </Box>
          <SectionContent
            initialSpacing={false}
            text={[
              {
                text: 'This balance of adaptability and discipline defines its institutional edge.',
                fontSize: '1.3rem',
                marginBottom: SECTION_SPACING
              }
            ]}
          />
        </Box>

        {/* Why STRAND Q Section */}
        <Box
          sx={{
            backgroundColor: THEME.COLORS.PRIMARY_BLUE,
            color: THEME.COLORS.NEUTRAL_WHITE,
            padding: { xs: 3, sm: 4, md: 5 },
            borderRadius: '8px',
            mb: SECTION_SPACING
          }}
        >
          <Typography
            variant='h2'
            sx={{
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
              fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.BOLD,
              fontSize: { xs: '1.8rem', sm: '2rem', md: '2.2rem' },
              color: THEME.COLORS.NEUTRAL_WHITE,
              mb: ELEMENT_SPACING
            }}
          >
            Why STRAND Q
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: '1.2rem', sm: '1.4rem' },
              fontStyle: 'italic',
              mb: ELEMENT_SPACING,
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY
            }}
          >
            Conviction without adaptability is fragility.
          </Typography>
          <Typography
            sx={{
              fontSize: '1.3rem',
              lineHeight: 1.8,
              mb: ELEMENT_SPACING,
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY
            }}
          >
            STRAND Q is built to listen, learn, and adjust, transforming data
            into discipline.
          </Typography>
          <Typography
            sx={{
              fontSize: '1.3rem',
              lineHeight: 1.8,
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY
            }}
          >
            For allocators seeking systematic strategies that endure, STRAND Q
            stands at the intersection of technology, precision, and capital
            integrity.
          </Typography>
        </Box>

        {/* Related Documents Section */}
        <Box sx={{ mb: SECTION_SPACING }}>
          <Typography
            variant='h2'
            sx={{
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
              fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.BOLD,
              fontSize: { xs: '1.8rem', sm: '2rem', md: '2.2rem' },
              color: THEME.COLORS.PRIMARY_BLUE,
              mb: ELEMENT_SPACING
            }}
          >
            Related Documents
          </Typography>
          <Grid container spacing={3} sx={{ justifyContent: 'center' }}>
            <Grid item xs={12} sm={6} md={4}>
              <Button
                variant='outlined'
                startIcon={<DescriptionIcon />}
                onClick={() => handleDocumentClick('full-factsheet')}
                sx={{
                  width: '100%',
                  height: '100%',
                  minHeight: '80px',
                  padding: 3,
                  borderColor: THEME.COLORS.PRIMARY_BLUE,
                  color: THEME.COLORS.PRIMARY_BLUE,
                  fontSize: '1.1rem',
                  fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.SEMI_BOLD,
                  textTransform: 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  '&:hover': {
                    borderColor: THEME.COLORS.PRIMARY_BLUE,
                    backgroundColor: THEME.COLORS.PRIMARY_BLUE_LIGHT + '22'
                  }
                }}
              >
                <Box>Full Factsheet</Box>
                <Box sx={{ fontSize: '0.85rem', fontWeight: 'normal' }}>
                  Complete strategy overview
                </Box>
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Button
                variant='outlined'
                startIcon={<DescriptionIcon />}
                onClick={() => handleDocumentClick('strategy-factsheet')}
                sx={{
                  width: '100%',
                  height: '100%',
                  minHeight: '80px',
                  padding: 3,
                  borderColor: THEME.COLORS.PRIMARY_BLUE,
                  color: THEME.COLORS.PRIMARY_BLUE,
                  fontSize: '1.1rem',
                  fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.SEMI_BOLD,
                  textTransform: 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  '&:hover': {
                    borderColor: THEME.COLORS.PRIMARY_BLUE,
                    backgroundColor: THEME.COLORS.PRIMARY_BLUE_LIGHT + '22'
                  }
                }}
              >
                <Box>Strategy Description</Box>
                <Box sx={{ fontSize: '0.85rem', fontWeight: 'normal' }}>
                  Key metrics & methodology
                </Box>
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Contact Section */}
        <Box
          sx={{
            borderTop: `1px solid ${THEME.COLORS.NEUTRAL_GRAY}`,
            paddingTop: ELEMENT_SPACING,
            paddingBottom: ELEMENT_SPACING,
            mt: SECTION_SPACING
          }}
        >
          <Typography
            variant='body2'
            sx={{
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
              fontSize: '1.2rem',
              color: THEME.COLORS.PRIMARY_BLUE,
              lineHeight: 1.6,
              mb: 2
            }}
          >
            Contact MarQ Capital –&nbsp;
            <ContactLink
              style={{
                color: THEME.COLORS.NEUTRAL_GRAY,
                textDecoration: 'underline',
                fontSize: '1rem'
              }}
            >
              Schedule Your Meeting
            </ContactLink>
          </Typography>
          <Typography
            variant='body2'
            sx={{
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
              fontSize: '0.95rem',
              fontStyle: 'italic',
              color: THEME.COLORS.NEUTRAL_GRAY,
              lineHeight: 1.6
            }}
          >
            This information has been prepared solely for informational purposes
            and is not an offer to buy or sell or a solicitation of an offer to
            buy or sell any security or instrument or to participate in any
            trading strategy. Past performance is not necessarily indicative of
            future results.
          </Typography>
        </Box>
      </Box>
    </>
  );
};

export default StrandQPage;
